import { useRef, type JSX } from 'react'
import type { ConditionTree } from '@kc3/conversations'
import RuleBuilder, { type FieldDef } from './RuleBuilder.js'
import ApprovalLevelsEditor, { type Level } from './ApprovalLevelsEditor.js'

export type OnRejection = 'close' | 'return_to_agent' | 'escalate'
export type OnTimeout = 'escalate' | 'auto_approve' | 'auto_reject'

/**
 * The full editable shape of an approval workflow. Mirrors the backend
 * approval-workflow payload (spec §5.3). Submission/validation is owned by
 * the parent page (Task 8) — this component is purely controlled.
 */
export interface WorkflowInput {
  name: string
  triggerCondition: ConditionTree
  levels: Level[]
  onRejection: OnRejection
  onTimeout: OnTimeout
  /** Managed by the parent page's active-toggle chrome; no control rendered here. */
  isActive: boolean
}

export interface ApprovalWorkflowEditorProps {
  value: WorkflowInput
  onChange: (next: WorkflowInput) => void
  /** Field catalog for the trigger RuleBuilder (domain='approval'). */
  fields: FieldDef[]
}

const ON_REJECTION_OPTIONS: ReadonlyArray<{ value: OnRejection; label: string }> = [
  { value: 'close', label: 'Close conversation' },
  { value: 'return_to_agent', label: 'Return to agent' },
  { value: 'escalate', label: 'Escalate' },
]

const ON_TIMEOUT_OPTIONS: ReadonlyArray<{ value: OnTimeout; label: string }> = [
  { value: 'escalate', label: 'Escalate' },
  { value: 'auto_approve', label: 'Auto-approve' },
  { value: 'auto_reject', label: 'Auto-reject' },
]

/**
 * Controlled editor for a single approval workflow.
 *
 * Composes:
 *   - a name text input,
 *   - a {@link RuleBuilder} for the trigger condition (domain='approval' —
 *     the catalog is supplied by the parent via `fields`),
 *   - an {@link ApprovalLevelsEditor} for the ordered approval levels,
 *   - an on-rejection radio group,
 *   - an on-timeout radio group.
 *
 * Every edit flows out through `onChange({ ...value, <field>: next })`.
 * This component does NOT submit — the parent page owns submission and
 * validation, and is NOT a ZodForm (it nests two controlled sub-editors).
 *
 * ### ApprovalLevelsEditor remount contract
 * `ApprovalLevelsEditor` initialises internal `rowTypes` state once at mount
 * and never re-derives it from `value` afterwards. If the parent swaps this
 * editor's `value` wholesale (e.g. the drawer opens a different workflow),
 * the levels sub-editor must be remounted so its row state matches the new
 * data. We cannot distinguish "external replacement" from "our own onChange
 * round-trip" by reference identity alone — both arrive as a fresh array. So
 * we record every levels array we ourselves emit; when a new `value.levels`
 * reference appears that did NOT come from our own emit, it is an external
 * replacement and we bump the levels editor's `key` to force a remount.
 * Round-trips of our own edits keep the key stable, preserving `rowTypes`.
 */
export default function ApprovalWorkflowEditor({
  value,
  onChange,
  fields,
}: ApprovalWorkflowEditorProps): JSX.Element {
  // Defensive remount key for ApprovalLevelsEditor. The parent is expected to
  // remount this whole editor per opened workflow; but if it instead keeps
  // this editor mounted and replaces `value` wholesale, the `levels` array
  // reference changes for a reason OTHER than our own onChange round-trip.
  // We bump the key only when a new array reference arrives that did not
  // originate from our own emit, by tracking the last array we emitted.
  const lastEmittedLevels = useRef<Level[] | null>(null)
  const levelsKey = useRef(0)
  const prevLevels = useRef<Level[] | null>(null)
  if (prevLevels.current !== value.levels) {
    if (
      prevLevels.current !== null &&
      value.levels !== lastEmittedLevels.current
    ) {
      // A wholesale external replacement — force ApprovalLevelsEditor remount.
      levelsKey.current += 1
    }
    prevLevels.current = value.levels
  }

  function emit(patch: Partial<WorkflowInput>): void {
    onChange({ ...value, ...patch })
  }

  function handleLevelsChange(nextLevels: Level[]): void {
    lastEmittedLevels.current = nextLevels
    emit({ levels: nextLevels })
  }

  return (
    <div className="awe flex flex-col gap-4">
      {/* Name */}
      <div className="flex flex-col gap-1">
        <label htmlFor="awe-name" className="text-sm font-medium text-gray-700">
          Name
        </label>
        <input
          id="awe-name"
          type="text"
          value={value.name}
          onChange={(e) => emit({ name: e.target.value })}
          className="rounded border border-gray-300 px-2 py-1 text-sm"
        />
      </div>

      {/* Trigger condition */}
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">
          Trigger condition
        </span>
        <RuleBuilder
          value={value.triggerCondition}
          onChange={(next) => emit({ triggerCondition: next })}
          fields={fields}
        />
      </div>

      {/* Approval levels */}
      <div className="flex flex-col gap-1">
        <span className="text-sm font-medium text-gray-700">
          Approval levels
        </span>
        <ApprovalLevelsEditor
          key={levelsKey.current}
          value={value.levels}
          onChange={handleLevelsChange}
          maxLevels={10}
        />
      </div>

      {/* On rejection */}
      <fieldset className="flex flex-col gap-1 border-0 p-0">
        <legend className="text-sm font-medium text-gray-700">
          On rejection
        </legend>
        <div className="flex flex-col gap-0.5">
          {ON_REJECTION_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                name="awe-on-rejection"
                value={opt.value}
                checked={value.onRejection === opt.value}
                onChange={() => emit({ onRejection: opt.value })}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>

      {/* On timeout */}
      <fieldset className="flex flex-col gap-1 border-0 p-0">
        <legend className="text-sm font-medium text-gray-700">
          On timeout
        </legend>
        <div className="flex flex-col gap-0.5">
          {ON_TIMEOUT_OPTIONS.map((opt) => (
            <label key={opt.value} className="flex items-center gap-1 text-sm">
              <input
                type="radio"
                name="awe-on-timeout"
                value={opt.value}
                checked={value.onTimeout === opt.value}
                onChange={() => emit({ onTimeout: opt.value })}
              />
              {opt.label}
            </label>
          ))}
        </div>
      </fieldset>
    </div>
  )
}
